import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import AMPageShell from './components/AMPageShell';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';

function MetricRow({ label, value, unit = '' }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="font-caption text-sm text-muted-foreground">{label}</span>
      <span className="font-data text-sm font-medium text-foreground">{value}{unit}</span>
    </div>
  );
}

function NarrativeBox({ label, content }) {
  return (
    <div className="bg-muted/30 rounded-md p-4">
      <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{label}</p>
      <p className="font-body text-sm text-foreground leading-relaxed">{content}</p>
    </div>
  );
}

export default function ReviewPacks({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const [activeAssetId, setActiveAssetId] = useState(assets?.[0]?.id);
  const asset = assets?.find((a) => a?.id === activeAssetId) || assets?.[0];
  const navigate = useNavigate();

  if (!asset) return <AMPageShell activeTab="/asset-management/review-packs"><p className="text-muted-foreground font-caption">No assets selected.</p></AMPageShell>;

  return (
    <AMPageShell activeTab="/asset-management/review-packs">
      <div className="flex gap-6">
        {/* Asset list sidebar */}
        <div className="w-56 flex-shrink-0">
          <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select Asset</p>
            </div>
            <div className="divide-y divide-border">
              {assets?.map((a) => (
                <button
                  key={a?.id}
                  onClick={() => setActiveAssetId(a?.id)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    a?.id === activeAssetId ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-muted/40'
                  }`}
                >
                  <p className="font-caption text-sm font-medium text-foreground truncate">{a?.name}</p>
                  <p className="font-caption text-xs text-muted-foreground">{a?.sector}</p>
                  <div className="mt-1"><ReviewStatusBadge status={a?.reviewStatus} size="xs" /></div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Review Pack content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Asset Profile */}
          <div className="bg-card border border-border rounded-lg shadow-xs p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.25rem' }}>{asset?.name}</h2>
                <p className="font-caption text-sm text-muted-foreground mt-0.5">{asset?.sector} · {asset?.subSector} · {asset?.country}</p>
              </div>
              <div className="flex items-center gap-2">
                <ReviewStatusBadge status={asset?.reviewStatus} />
                <button
                  onClick={() => navigate(`/portfolio/assets/${asset?.id}`, { state: { from: 'asset-management', backPath: '/asset-management/review-packs', backLabel: 'Review Packs' } })}
                  className="flex items-center gap-1.5 text-xs font-caption text-primary hover:underline px-2 py-1 rounded-md hover:bg-primary/5 transition-colors"
                >
                  <Icon name="ExternalLink" size={12} color="var(--color-primary)" />
                  Asset Detail
                </button>
              </div>
            </div>
            <p className="font-body text-sm text-foreground leading-relaxed mb-4">{asset?.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Fund', value: asset?.fund?.replace('SeedDrive ', '') },
                { label: 'Stage', value: asset?.stage },
                { label: 'Shareholding', value: `${asset?.shareholdingPct}%` },
                { label: 'Total Investment', value: `£${asset?.totalInvestmentValue}m` },
                { label: 'Acquisition Date', value: asset?.acquisitionDate },
                { label: 'Last Review', value: asset?.lastReviewDate },
                { label: 'Fund Exposure', value: `£${asset?.fundExposure}m` },
                { label: 'Debt Stack', value: asset?.debtStack ? 'Yes' : 'No' },
              ]?.map((f) => (
                <div key={f?.label} className="bg-muted/30 rounded-md p-3">
                  <p className="font-caption text-xs text-muted-foreground">{f?.label}</p>
                  <p className="font-caption text-sm font-semibold text-foreground mt-0.5">{f?.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Expiry Dates</p>
                {asset?.keyExpiryDates?.map((d) => (
                  <div key={d?.label} className="flex justify-between text-sm font-caption py-1 border-b border-border last:border-0">
                    <span className="text-muted-foreground">{d?.label}</span>
                    <span className="font-data text-foreground">{d?.date}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Operational Dates</p>
                {asset?.keyOperationalDates?.map((d) => (
                  <div key={d?.label} className="flex justify-between text-sm font-caption py-1 border-b border-border last:border-0">
                    <span className="text-muted-foreground">{d?.label}</span>
                    <span className="font-data text-foreground">{d?.date}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Section */}
          <div className="bg-card border border-border rounded-lg shadow-xs p-6">
            <h3 className="font-heading font-semibold text-foreground mb-4" style={{ fontSize: '1.05rem' }}>Performance</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Investment IRR', value: `${asset?.irr}%` },
                { label: 'Yield (Yrs 1–10)', value: `${asset?.yield1to10}%` },
                { label: 'Yield (Yrs 11–20)', value: `${asset?.yield11to20}%` },
                { label: 'Yield (Yrs 21–25)', value: `${asset?.yield21to25}%` },
                { label: 'Lifetime Yield', value: `${asset?.yieldLifetime}%` },
                { label: 'Multiple', value: `${asset?.multiple}x` },
                { label: 'Cash Yield (Actual LTM)', value: `${asset?.cashYieldActual}%` },
                { label: 'Cash Yield (Budget NTM)', value: `${asset?.cashYieldBudget}%` },
              ]?.map((m) => (
                <div key={m?.label} className="bg-muted/30 rounded-md p-3">
                  <p className="font-caption text-xs text-muted-foreground">{m?.label}</p>
                  <p className="font-data text-lg font-semibold text-foreground mt-0.5">{m?.value}</p>
                </div>
              ))}
            </div>
            {/* IRR Evolution Chart */}
            <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">IRR Evolution</p>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={asset?.irrHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }} />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }} unit="%" />
                <Tooltip contentStyle={{ fontFamily: 'IBM Plex Sans', fontSize: 12 }} formatter={(v) => [`${v}%`, 'IRR']} />
                <Line type="monotone" dataKey="irr" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Narrative Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NarrativeBox label="Outperformance Drivers" content={asset?.outperformanceDrivers} />
            <NarrativeBox label="Underperformance Drivers" content={asset?.underperformanceDrivers} />
            <NarrativeBox label="Asset Timeline" content={asset?.assetTimeline} />
          </div>

          {/* Review Summary */}
          <div className="bg-card border border-border rounded-lg shadow-xs p-6">
            <h3 className="font-heading font-semibold text-foreground mb-4" style={{ fontSize: '1.05rem' }}>Review Summary</h3>
            <div className="space-y-4">
              <NarrativeBox label="Summary" content={asset?.reviewSummary} />
              <NarrativeBox label="Agreed Actions" content={asset?.agreedActions} />
              <div className="flex items-center gap-3">
                <Icon name="Calendar" size={15} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="font-caption text-sm text-muted-foreground">Next review: </span>
                <span className="font-data text-sm font-medium text-foreground">{asset?.nextReviewDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
